import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { tokenApi } from "../apis/token-api";
import { setAuthToken } from "../apis/instance";
import gopoLogo from "../images/gopo-logo.png";
import { ReactComponent as Loading } from "../images/loading.svg";
interface ILogin {
  setUserLogin: Function;
  admin?: boolean;
}
declare global {
  interface Window {
    google: any;
  }
}
declare const google: any;
const Login = ({ setUserLogin, admin = false }: ILogin) => {
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<string>("");
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef<(response: any) => Promise<void> | void>(
    () => {},
  );
  const responseGoogleLoginSucceess = async (response: any) => {
    setIsLoading(true);
    setAlert("");
    const userData = JSON.parse(window.atob(response.credential.split(".")[1]));
    const result = {
      email: userData.email,
      name: userData.name,
      givenName: userData.given_name,
      familyName: userData.family_name,
      imageUrl: userData.picture,
      idToken: response.credential,
      domain: userData.hd,
    };

    try {
      setAuthToken(result.idToken);
      const data = await tokenApi.validation(result.idToken, admin);
      if (admin) {
        setUserLogin({ ...result });
        return;
      }
      if (!data?.expiryDate) {
        setAlert(
          "Your account is not registered with any company. Please contact administrator.",
        );
        setUserLogin(undefined);
        setAuthToken(undefined);
        response.disconnect?.();
        return;
      }
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      if (
        isLocalhost ||
        new Date(data.expiryDate) > new Date(new Date().setHours(0, 0, 0, 0))
      ) {
        setUserLogin({ ...result, company: data.companyName });
        return;
      }
      setAlert(
        `Your subscription has expired on ${new Date(
          data.expiryDate,
        ).toLocaleDateString()}. Please contact administrator.`,
      );
      setUserLogin(undefined);
      setAuthToken(undefined);
      response.disconnect?.();
    } catch (err: any) {
      const message =
        err?.response?.data ||
        err?.message ||
        "Cannot reach the server. Please try again.";
      setAlert(typeof message === "string" ? message : JSON.stringify(message));
      setUserLogin(undefined);
      setAuthToken(undefined);
      response.disconnect?.();
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    callbackRef.current = responseGoogleLoginSucceess;
  });
  useEffect(() => {
    if (typeof google === "undefined" || !google?.accounts?.id) return;
    google.accounts.id.initialize({
      client_id: process.env.REACT_APP_OAUTH_CLIENT_SECRET,
      callback: (e: any) => callbackRef.current(e),
    });
    if (buttonRef.current) {
      buttonRef.current.innerHTML = "";
      google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        width: 320,
      });
    }
    google.accounts.id.prompt((notification: any) => {
      // eslint-disable-next-line no-console
      console.log("on prompt notification", notification);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);
  return (
    <SContainer>
      <SCard>
        <SLogo src={gopoLogo} alt="GoPomelo" />
        <SLabel>
          {admin
            ? "Welcome to Administration Portal"
            : "Welcome to Email Log Search"}
        </SLabel>
        <SHelper>Sign in with your Google account to continue</SHelper>
        <SButtonSlot>
          <SButtonWrapper
            ref={buttonRef}
            id="buttonDiv"
            data-client_id={`${process.env.REACT_APP_OAUTH_CLIENT_SECRET}`}
            style={{ visibility: isLoading ? "hidden" : "visible" }}
          />
          {isLoading && (
            <SLoadingWrapper>
              <Loading />
            </SLoadingWrapper>
          )}
        </SButtonSlot>
        {alert && <SAlert>{alert}</SAlert>}
      </SCard>
    </SContainer>
  );
};
export default Login;
const SContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background:
    radial-gradient(
      1200px 600px at 10% -10%,
      rgba(60, 127, 236, 0.18),
      transparent 60%
    ),
    radial-gradient(
      900px 500px at 110% 110%,
      rgba(16, 185, 129, 0.14),
      transparent 55%
    ),
    linear-gradient(180deg, #f6f8fc 0%, #eef2fb 100%);
`;
const SCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  padding: 48px 40px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: rise 320ms cubic-bezier(0.4, 0, 0.2, 1);
  @keyframes rise {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
const SLogo = styled.img`
  height: 56px;
  width: auto;
  object-fit: contain;
  margin-bottom: 24px;
`;
const SLabel = styled.div`
  font-family: "Poppins", "Inter", sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: var(--color-text);
  text-align: center;
  letter-spacing: -0.01em;
`;
const SHelper = styled.div`
  color: var(--color-text-muted);
  font-size: 14px;
  margin: 8px 0 32px;
  text-align: center;
`;
const SButtonSlot = styled.div`
  position: relative;
  min-height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
const SButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const SLoadingWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const SAlert = styled.div`
  margin-top: 20px;
  color: var(--color-danger);
  background: var(--color-danger-soft);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  font-size: 13px;
  text-align: center;
  width: 100%;
`;
